import {useState, useEffect} from 'react';
import {Stack, Box, TextField, useMediaQuery} from '@mui/material';
// DateField and LocalizationProvider imports removed - using custom date inputs for large screens
import dayjs from 'dayjs';
import {useClient} from '../../../../context/ClientContext';
import ScrollableDatePicker from '../../../dateInput/scrollableDatePicker';
import ClientButton from '../../common/Clientbutton';
import CommonErrorMessage from '../../common/CommonErrorMessage';
import StepsHeading from '../../common/StepsHeading';

export default function Step2_DOB({userData, setUserData, onNext}) {
  const {theme} = useClient();
  const isLargeScreen = useMediaQuery('(min-width:600px)');

  const [validation, setValidation] = useState({
    isDateOfBirthValidate: {isTrue: true, errorMsg: ''},
    isDayValidated: {isTrue: true, errorMsg: ''},
    isMonthValidated: {isTrue: true, errorMsg: ''},
    isYearValidated: {isTrue: true, errorMsg: ''},
  });

  // Initialize individual date fields in userData if not present
  useEffect(() => {
    if (userData.dateOfBirth && dayjs(userData.dateOfBirth).isValid()) {
      const dobDate = dayjs(userData.dateOfBirth);
      if (!userData.dobDay || !userData.dobMonth || !userData.dobYear) {
        setUserData({
          ...userData,
          dobDay: dobDate.format('DD'),
          dobMonth: dobDate.format('MM'),
          dobYear: dobDate.format('YYYY'),
        });
      }
    }
  }, [userData.dateOfBirth]);

  // Function to calculate age from date of birth
  function calculateAge(birthDate) {
    const birth = new Date(birthDate);
    const today = new Date();

    let age = today.getFullYear() - birth.getFullYear();

    // Check if birthday has occurred this year
    const monthDiff = today.getMonth() - birth.getMonth();
    const dayDiff = today.getDate() - birth.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age--;
    }

    return age;
  }

  const handleDateChange = newValue => {
    setUserData({...userData, dateOfBirth: newValue});
    //setUserData({ ...userData, age: calculateAge(newValue) });

    // Clear validation error when user changes the value
    if (!validation.isDateOfBirthValidate.isTrue) {
      setValidation({
        ...validation,
        isDateOfBirthValidate: {isTrue: true, errorMsg: ''},
      });
    }
  };

  // Handle individual date field changes for large screens
  const handleDateInputChange = (field, value) => {
    // Remove any non-numeric characters
    const numericValue = value.replace(/\D/g, '');

    // Apply length limits
    let limitedValue = numericValue;
    if (field === 'dobDay' || field === 'dobMonth') {
      limitedValue = numericValue.slice(0, 2);
    } else if (field === 'dobYear') {
      limitedValue = numericValue.slice(0, 4);
    }

    // Clear validation errors as user types
    setValidation(prev => ({
      ...prev,
      isDayValidated: {isTrue: true, errorMsg: ''},
      isMonthValidated: {isTrue: true, errorMsg: ''},
      isYearValidated: {isTrue: true, errorMsg: ''},
      isDateOfBirthValidate: {isTrue: true, errorMsg: ''},
    }));

    // Update user data and create date object if all fields are filled
    setUserData(prev => {
      const newData = {
        ...prev,
        [field]: limitedValue,
      };

      // Check if all date fields are filled
      if (newData.dobDay && newData.dobMonth && newData.dobYear) {
        const dateString = `${newData.dobYear}-${newData.dobMonth.padStart(2, '0')}-${newData.dobDay.padStart(2, '0')}`;
        const dateObj = dayjs(dateString);
        if (dateObj.isValid()) {
          newData.dateOfBirth = dateObj;
        }
      }

      return newData;
    });
  };

  // Validate individual date fields
  const validateDateFields = () => {
    const currentYear = new Date().getFullYear();
    const updatedValidation = {...validation};
    let hasError = false;

    // Validate day
    const day = parseInt(userData.dobDay);
    if (!userData.dobDay) {
      updatedValidation.isDayValidated = {isTrue: false, errorMsg: 'Day required'};
      hasError = true;
    } else if (day < 1 || day > 31) {
      updatedValidation.isDayValidated = {isTrue: false, errorMsg: 'Invalid day'};
      hasError = true;
    } else {
      updatedValidation.isDayValidated = {isTrue: true, errorMsg: ''};
    }

    // Validate month
    const month = parseInt(userData.dobMonth);
    if (!userData.dobMonth) {
      updatedValidation.isMonthValidated = {isTrue: false, errorMsg: 'Month required'};
      hasError = true;
    } else if (month < 1 || month > 12) {
      updatedValidation.isMonthValidated = {isTrue: false, errorMsg: 'Invalid month'};
      hasError = true;
    } else {
      updatedValidation.isMonthValidated = {isTrue: true, errorMsg: ''};
    }

    // Validate year
    const year = parseInt(userData.dobYear);
    if (!userData.dobYear) {
      updatedValidation.isYearValidated = {isTrue: false, errorMsg: 'Year required'};
      hasError = true;
    } else if (year < 1900 || year > currentYear) {
      updatedValidation.isYearValidated = {isTrue: false, errorMsg: `Year must be between 1900-${currentYear}`};
      hasError = true;
    } else {
      updatedValidation.isYearValidated = {isTrue: true, errorMsg: ''};
    }

    // Validate complete date if individual fields are valid
    if (!hasError) {
      const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const dateObj = dayjs(dateString);

      if (!dateObj.isValid()) {
        updatedValidation.isDateOfBirthValidate = {isTrue: false, errorMsg: 'Invalid date combination'};
        hasError = true;
      } else {
        // Check age
        const age = currentYear - year;
        const hasHadBirthday = dayjs().isAfter(dateObj.add(age, 'year'));
        const actualAge = hasHadBirthday ? age : age - 1;

        if (actualAge < 13 || actualAge > 120) {
          updatedValidation.isDateOfBirthValidate = {isTrue: false, errorMsg: 'Age must be between 13 and 120 years'};
          hasError = true;
        } else {
          updatedValidation.isDateOfBirthValidate = {isTrue: true, errorMsg: ''};
          // Update dateOfBirth if valid
          setUserData({...userData, dateOfBirth: dateObj});
        }
      }
    }

    return {isValid: !hasError, validation: updatedValidation};
  };

  const handleNextStep = () => {
    if (isLargeScreen) {
      // For large screens, validate individual date fields
      const dateValidation = validateDateFields();
      setValidation(dateValidation.validation);

      if (!dateValidation.isValid) {
        return;
      }

      // Set age and proceed
      const dob = userData.dateOfBirth;
      setUserData({...userData, age: calculateAge(dob)});
      onNext();
    } else {
      // For mobile screens, validate dateOfBirth
      const dob = userData.dateOfBirth;
      setUserData({...userData, age: calculateAge(dob)});

      if (!dob) {
        setValidation({
          ...validation,
          isDateOfBirthValidate: {isTrue: false, errorMsg: 'Please enter your date of birth.'},
        });
        return;
      }

      // Check dayjs validity
      if (!dayjs(dob).isValid()) {
        setValidation({
          ...validation,
          isDateOfBirthValidate: {isTrue: false, errorMsg: 'Please enter a valid date of birth.'},
        });
        return;
      }

      // Calculate age and validate range (13-120)
      const age = calculateAge(dob);
      if (age < 13 || age > 120) {
        setValidation({
          ...validation,
          isDateOfBirthValidate: {isTrue: false, errorMsg: 'Age must be between 13 and 120 years.'},
        });
        return;
      }

      // All validations passed, proceed to next step
      onNext();
    }
  };

  return (
    <Stack spacing={3} alignItems="center">
      <StepsHeading title={'What is your date of birth?'} />

      <Stack
        sx={{
          width: {
            xs: '260px',
            sm: '300px',
            md: '350px',
            lg: '400px',
          },
          marginY: '30px',
        }}
      >
        {isLargeScreen ? (
          <Box sx={{marginTop: '20px'}}>
            <Box sx={{display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'flex-start'}}>
              {/* Day Input */}
              <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <TextField
                  label="DD"
                  variant="outlined"
                  type="text"
                  value={userData.dobDay || ''}
                  onChange={e => handleDateInputChange('dobDay', e.target.value)}
                  inputProps={{maxLength: 2}}
                  sx={{
                    width: '80px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': {
                        borderColor: theme.primaryColor || '#000000',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.primaryColor || '#000000',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.primaryColor || '#000000',
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.primaryColor || '#000000',
                      '&.Mui-focused': {
                        color: theme.primaryColor || '#000000',
                      },
                    },
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: '500',
                    },
                  }}
                />
                {!validation.isDayValidated.isTrue && (
                  <CommonErrorMessage text={validation.isDayValidated.errorMsg} sx={{fontSize: '12px', mt: 1}} />
                )}
              </Box>

              {/* Month Input */}
              <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <TextField
                  label="MM"
                  variant="outlined"
                  type="text"
                  value={userData.dobMonth || ''}
                  onChange={e => handleDateInputChange('dobMonth', e.target.value)}
                  inputProps={{maxLength: 2}}
                  sx={{
                    width: '80px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': {
                        borderColor: theme.primaryColor || '#000000',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.primaryColor || '#000000',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.primaryColor || '#000000',
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.primaryColor || '#000000',
                      '&.Mui-focused': {
                        color: theme.primaryColor || '#000000',
                      },
                    },
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: '500',
                    },
                  }}
                />
                {!validation.isMonthValidated.isTrue && (
                  <CommonErrorMessage text={validation.isMonthValidated.errorMsg} sx={{fontSize: '12px', mt: 1}} />
                )}
              </Box>

              {/* Year Input */}
              <Box sx={{display: 'flex', flexDirection: 'column'}}>
                <TextField
                  label="YYYY"
                  variant="outlined"
                  type="text"
                  value={userData.dobYear || ''}
                  onChange={e => handleDateInputChange('dobYear', e.target.value)}
                  inputProps={{maxLength: 4}}
                  sx={{
                    width: '100px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': {
                        borderColor: theme.primaryColor || '#000000',
                      },
                      '&:hover fieldset': {
                        borderColor: theme.primaryColor || '#000000',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.primaryColor || '#000000',
                        borderWidth: '2px',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: theme.primaryColor || '#000000',
                      '&.Mui-focused': {
                        color: theme.primaryColor || '#000000',
                      },
                    },
                    '& .MuiInputBase-input': {
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: '500',
                    },
                  }}
                />
                {!validation.isYearValidated.isTrue && (
                  <CommonErrorMessage text={validation.isYearValidated.errorMsg} sx={{fontSize: '12px', mt: 1}} />
                )}
              </Box>
            </Box>

            {/* General date validation error */}
            {!validation.isDateOfBirthValidate.isTrue && (
              <Box sx={{mt: 2, textAlign: 'center'}}>
                <CommonErrorMessage text={validation.isDateOfBirthValidate.errorMsg} />
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{marginTop: '20px'}}>
            <ScrollableDatePicker onDateChange={handleDateChange} initialDate={userData.dateOfBirth} />
          </Box>
        )}

        {/* Mobile validation error */}
        {!isLargeScreen && (
          <Box sx={{marginTop: '50px'}}>
            <CommonErrorMessage
              text={!validation.isDateOfBirthValidate.isTrue && validation.isDateOfBirthValidate.errorMsg}
            />
          </Box>
        )}
      </Stack>

      <ClientButton
        text="Next"
        onClick={handleNextStep}
        backgroundColor={theme.primaryColor}
        sx={{
          maxWidth: '320px',
          width: '100%',
          marginTop: {xs: '4px', sm: '30px', md: '20px', lg: '20px'},
        }}
      />
    </Stack>
  );
}
